import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Sparkles, Clock, XCircle, Mail } from "lucide-react";

interface GiftCardInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "info" | "confirmation";
  onConfirm?: () => void;
}

const GiftCardInfoModal = ({ open, onOpenChange, mode, onConfirm }: GiftCardInfoModalProps) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md border-pink-200">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-semibold text-bbz-text flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-pink-500" />À savoir avant de commander
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {/* Information principale - Institut réservé aux femmes */}
              <div className="bg-pink-50 dark:bg-pink-950 border-2 border-pink-300 dark:border-pink-800 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <Badge
                      variant="default"
                      className="mb-2 bg-pink-100 text-pink-800 border-pink-300 hover:bg-pink-200"
                    >
                      IMPORTANT
                    </Badge>
                    <p className="text-lg font-bold text-pink-700 dark:text-pink-400 leading-tight">
                      Institut réservé à une clientèle féminine
                    </p>
                  </div>
                </div>
              </div>

              {/* Informations complémentaires */}
              <div className="space-y-2.5 text-sm text-muted-foreground">
                <p className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Informations complémentaires :
                </p>

                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-medium text-foreground">Envoi :</span> Le bon cadeau vous sera envoyé par
                    email immédiatement après le paiement
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-medium text-foreground">Validité :</span> 6 mois à partir de la confirmation
                    du paiement (sauf cas exceptionnels)
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-medium text-foreground">Non remboursable :</span> Les bons cadeaux ne sont ni
                    échangeables, ni remboursables
                  </p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {mode === "confirmation" && (
            <AlertDialogCancel onClick={() => onOpenChange(false)}>Annuler</AlertDialogCancel>
          )}
          <AlertDialogAction
            onClick={() => {
              if (mode === "confirmation" && onConfirm) {
                onConfirm();
              } else {
                onOpenChange(false);
              }
            }}
            className="bg-bbz-green hover:bg-bbz-green/90"
          >
            {mode === "confirmation" ? "Je confirme et continue" : "J'ai compris"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default GiftCardInfoModal;
